import Component from "../../lib/component";
import { JsonFetcher } from "../../lib/json_fetcher";
import { MixinPersistentStore } from "../../lib/mixins/persistable";
import FormSerialize from "form-serialize";
import Submission from "./models/submission";
import { Message } from "./components/message";
import {isNull, isUndefined} from "../../helpers/object";

export const MemberOfferAcceptorConfig = {
  componentTypeKey: "MemberOfferAcceptor",
  baseHtmlClass: "member_offer_acceptor",
  selector: ".member_offer_acceptor",

  // Dataset config
  datasetConfigKey: "member_offer_acceptor__config",
  datasetConfigEndpointKey: "endpoint",
  datasetConfigLocalStorageKey: "local_storage_key",

  // Local Storage
  defaultLocalStorageKey: "MEMBER_OFFER_ACCEPTOR",
  storedSubmissionsKey: "submissions",

  // Form
  formObjectName: "member_offer_acceptance",
  // Field Selectors
  idInputSelector: ".member_offer_acceptor__ii input",
  guidInputSelector: ".member_offer_acceptor__gi input",
  emailInputSelector: ".member_offer_acceptor__ei input",
  nameInputSelector: ".member_offer_acceptor__ni input",
  memberOfferCodeInputSelector: ".member_offer_acceptor__moci input",
  memberTierCodeInputSelector: ".member_offer_acceptor__mtci input",
  submitButtonInputSelector: ".member_offer_acceptor__sb input",
};

export const EndpointStatuses = {
  idle: "idle",
  loading: "loading",
  error: "error",
  success: "success",
  failure: "failure",
};

export class MemberOfferAcceptor extends Component {
  static get config() {
    return MemberOfferAcceptorConfig;
  }

  constructor(id_or_element) {
    super({
      element: id_or_element,
      config: MemberOfferAcceptor.config,
    });

    // Get config from the dataset
    this.setDatasetConfig(MemberOfferAcceptor.config.datasetConfigKey);

    this.endpoint = this.getDatasetConfigValue(
      MemberOfferAcceptor.config.datasetConfigEndpointKey,
    );

    const localStorageKey = this.getDatasetConfigValue(
      MemberOfferAcceptor.config.datasetConfigLocalStorageKey,
    ) || MemberOfferAcceptor.config.defaultLocalStorageKey;

    // Persistent local storage for the submissions to the endpoint (and responses).
    MixinPersistentStore(this);
    this.setPersistentStore(window.localStorage);
    this.setPersistentStorageKey(localStorageKey);
    this.setStoreItem("lastConstructedAt", new Date().toISOString());
    this.submissions = this.getStoredSubmissions();

    // Configure some sub-components and elements
    this.formElement = this.element.querySelector("form");
    this.message = getMessage(this);

    // Update the form with any previous submission from local storage
    const lastSubmission = this.getLastSubmission();
    if (lastSubmission) this.updateFormFields(lastSubmission);

    // Set up state…
    this.setEndpointStatus(EndpointStatuses.idle);

    // Listen for form submissions
    this.formElement.addEventListener("submit", this.handleSubmit.bind(this));

    this.render();
  }

  handleSubmit(event) {
    event.preventDefault();

    const jsonFetcher = new JsonFetcher(this.endpoint, {
      httpMethod: this.formElement.method,
      timeoutCallback: this.handleEndpointLateData.bind(this),
      timeout: 10000,
    });
    let params = this.getFormParams();

    // Set up the component state for the submission request.
    this.setEndpointStatus(EndpointStatuses.loading);
    this.message.setContent("Connecting to Rankers.co.nz…");

    // Create a timestamped `Submission` for local storage.
    const submission = Submission.newFromParams(
      params[MemberOfferAcceptor.config.formObjectName],
    );
    this.storeSubmission(submission);

    // Incorporate the Submission GUID into the submission parameters along with the form data.
    params[MemberOfferAcceptor.config.formObjectName]["guid"] = submission.guid;

    jsonFetcher
      .fetchData(params)
      .then(this.handleEndpointData.bind(this))
      .catch(this.handleEndpointDataError.bind(this))
      .finally(this.render.bind(this));

    this.render();
  }

  // Get the form's input values as a nested object while ignoring empty inputs.
  // Eg. { member_offer_acceptance: { email: "foo@bar", name: "Foo Bar" } }
  getFormParams() {
    return FormSerialize(this.formElement, { hash: true, empty: false });
  }

  handleEndpointDataError(error) {
    console.error("[MemberOfferAcceptor]", error);
    this.setEndpointStatus(EndpointStatuses.error);
    this.message.setContent(
      "An error occurred. Perhaps you have no network access. Please try again.",
    );
    this.render();
  }

  // This gets called after a timeout, when the server has taken too long to respond but does eventually respond.
  handleEndpointLateData(data) {
    this.setEndpointStatus(EndpointStatuses.error);
    this.message.setContent(
      "The server did not respond in time. Do you have internet? Please try again.",
    );
    this.render();
  }

  handleEndpointData(data) {
    if (typeof data !== "object") {
      throw new Error(
        "[MemberOfferAcceptor] handleEndpointData() `data` must be an object",
      );
    }

    // Our message component will show the server's message.
    this.message.setContent(data.message);

    // The response contains our `MemberOfferAcceptance` data.
    const moaData = data[MemberOfferAcceptor.config.formObjectName];

    if (data.status === "success" && moaData) {
      this.setEndpointStatus(EndpointStatuses.success);

      const submission = this.submissions.find(
        (submission) => submission.guid === moaData.guid,
      );
      if (submission) {
        submission.updateFromServer(moaData);
        this.updateFormFields(submission);
      }

      // Because the submission data was updated, we need to update the stored submissions.
      this.setStoreItem(
        MemberOfferAcceptor.config.storedSubmissionsKey,
        this.submissions,
      );
    } else {
      this.setEndpointStatus(EndpointStatuses.failure);
    }

    this.render();
  }

  render() {
    this.selectHtmlClassState(
      this.getEndpointStatus(),
      Object.values(EndpointStatuses),
    ); // Set the state of the component based on the endpoint status.

    this.conditionallySetHtmlClassState(this.hasResponse(), "has_response");

    // Disable the form inputs if the submission was successful.
    const isDisabled = !!this.submissionWasSuccessful();
    getEmailInput(this).disabled = isDisabled;
    getNameInput(this).disabled = isDisabled;
    getSubmitInput(this).disabled = isDisabled;

    if (this.message) this.message.render();
  }

  getEndpointStatus() {
    return this.endpointStatus || EndpointStatuses.idle;
  }

  setEndpointStatus(status) {
    this.endpointStatus = status;
  }

  submissionWasSuccessful() {
    return this.getEndpointStatus() === EndpointStatuses.success;
  }

  hasResponse() {
    return (
      this.getEndpointStatus() === EndpointStatuses.success ||
      this.getEndpointStatus() === EndpointStatuses.failure ||
      this.getEndpointStatus() === EndpointStatuses.error
    );
  }

  storeSubmission(submission) {
    this.submissions.push(submission);
    this.setStoreItem(
      MemberOfferAcceptor.config.storedSubmissionsKey,
      this.submissions,
    );
  }

  // Hydrate any previous submissions from local storage.
  getStoredSubmissions() {
    const submissionsProps =
      this.getStoreItem(MemberOfferAcceptor.config.storedSubmissionsKey) || [];
    return submissionsProps.map((props) => new Submission(props));
  }

  getLastSubmission() {
    return this.submissions[this.submissions.length - 1];
  }

  // Directly modify the form input values from the Submission object.
  updateFormFields(submission) {
    this.element.querySelector(
      MemberOfferAcceptor.config.idInputSelector,
    ).value = inputValue(submission.id);
    this.element.querySelector(
      MemberOfferAcceptor.config.guidInputSelector,
    ).value = inputValue(submission.guid);
    this.element.querySelector(
      MemberOfferAcceptor.config.emailInputSelector,
    ).value = inputValue(submission.email);
    this.element.querySelector(
      MemberOfferAcceptor.config.memberOfferCodeInputSelector,
    ).value = inputValue(submission.memberOfferCode);
    this.element.querySelector(
      MemberOfferAcceptor.config.memberTierCodeInputSelector,
    ).value = inputValue(submission.memberTierCode);
    this.element.querySelector(
      MemberOfferAcceptor.config.nameInputSelector,
    ).value = inputValue(submission.name);
  }
}

function getEmailInput(memberOfferAcceptor) {
  const element = memberOfferAcceptor.element.querySelector(
    MemberOfferAcceptor.config.emailInputSelector,
  );
  if (element.type !== "email") element.type = "email"; // Necessary because the `type` is not set in the HTML (due to `Formtastic` gem not supporting the `email` type).
  return element;
}

function getNameInput(memberOfferAcceptor) {
  return memberOfferAcceptor.element.querySelector(
    MemberOfferAcceptor.config.nameInputSelector,
  );
}

function getSubmitInput(memberOfferAcceptor) {
  return memberOfferAcceptor.element.querySelector(
    MemberOfferAcceptor.config.submitButtonInputSelector,
  );
}

function getMessage(memberOfferAcceptor) {
  const selector = Message.config.selector;
  const element = memberOfferAcceptor.element.querySelector(selector);
  return new Message(element, memberOfferAcceptor);
}

function inputValue(v) {
  if (!isUndefined(v) && !isNull(v) && v !== "") return v;
  return "";
}